// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useMemo, useCallback } from 'react';

import type { HexTriplet } from '@evolvemep/api-ts';
import { useMantineTheme, ActionIcon } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useUncontrolled } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { convertMantineColorToHex } from 'components/Mantine/ColorPickers/colorHelpers';
import { ColorAndNameFields } from 'components/Mantine/Labels/ManageLabelsModal/ColorAndNameFields';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { isNotNil } from 'helpers/isNotNil';

import type { CustomStatus, CustomStatusEntityName, StateIdFor } from './types';
import { useCustomStatuses } from './useCustomStatuses';

type Props<TEntity extends CustomStatusEntityName> = {
  customStatusTypeName: TEntity;
  /**
   * The element that opens the popover (e.g. a button or icon).
   *
   * If not provided, will render an "Add" button.
   * */
  target?: ReactNode;
  opened?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  stateId: StateIdFor<TEntity>;
  status?: CustomStatus<TEntity>;
};

type FormValues = {
  /** Status name */
  name: string;
  color: HexTriplet;
};

const StatusFields = <T extends CustomStatusEntityName>({
  customStatusTypeName,
  stateId,
  status,
}: Pick<Props<T>, 'customStatusTypeName' | 'stateId' | 'status'>) => {
  const { addStatus, updateStatus, getLabelAndColor, states } = useCustomStatuses(customStatusTypeName);

  const theme = useMantineTheme();
  const { color: defaultColor } = useMemo(() => {
    const state = states?.find((s) => s.stateId === stateId);
    return isNotNil(state) ? getLabelAndColor(state.name) : { color: 'gray.8' };
  }, [states, stateId, getLabelAndColor]);

  const form = useForm<FormValues>({
    initialValues: {
      name: status?.name ?? '',
      color: status?.color ?? convertMantineColorToHex(defaultColor, theme.colors),
    },
    validate: {
      name: (value) => !value,
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    if (isNotNil(status)) {
      await updateStatus(status.statusId, { name: values.name, color: values.color });
    } else {
      await addStatus({ stateId, name: values.name, color: values.color });
      notifications.show({
        title: 'Successfully created status',
        message: `Status ${values.name} created.`,
        color: 'green',
      });
    }
  };

  useFormPopover(form, onSubmit);

  return <ColorAndNameFields form={form} maxNameLength={32} placeholder="Custom status name" />;
};

/**
 * Popover for creating or editing a custom status.
 */
export const AddEditStatusPopover = <T extends CustomStatusEntityName>({
  customStatusTypeName,
  target,
  opened: parentOpened,
  onOpen: parentOnOpen,
  onClose: parentOnClose,
  stateId,
  status,
}: Props<T>) => {
  const [opened, setOpened] = useUncontrolled({
    defaultValue: false,
    value: parentOpened,
    onChange: (v) => {
      if (v) {
        parentOnOpen?.();
      } else {
        parentOnClose?.();
      }
    },
  });
  const onClose = useCallback(() => setOpened(false), [setOpened]);

  return (
    <FormPopover
      opened={opened}
      onClose={onClose}
      target={
        target ?? (
          <ActionIcon
            component="div"
            variant="light"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setOpened(true);
            }}
          >
            <EvolveIcon icon="Add" size="xs" />
          </ActionIcon>
        )
      }
      withCloseButton={false}
    >
      <StatusFields customStatusTypeName={customStatusTypeName} stateId={stateId} status={status} />
    </FormPopover>
  );
};
